/**
 * This file governs how store size is monitored for client-side normalized
 * Redux data. We monitor this to get ahead of potential performance issues.
 *
 * As of 2025-08-08, the store for our weekly test class course weighs
 * 500kB on the main page, and 750kB with all the assessment data loaded.
 * If our store selectors cache correctly (which isn't always a given, we
 * should make that easier), then we're mostly worried about browser memory
 * requirements, as opposed to CPU requirements (everything is very efficient
 * in terms of runtime).
 *
 * (Peter)
 * Note that actual memory use is about ~5x the store size, due to various
 * overhead in JS and Redux. I suspect we might be able to have a store at
 * about ~50MB (100x our current test course size) before memory requirements
 * (250MB+ just from store) become a potential performance issue for older
 * machines. On our macbooks, I think we probably wouldn't notice even if the
 * store exceed 500MB (2.5GB memory).
 *
 * Our local-first approach of having (essentially) a client side DB by using
 * Redux+storeAPI+normalized endpoints is designed to be simple to develop on.
 * It's designed to pull all the complexity of querying into redux selectors,
 * so that pubsub "just works". We chose this approach knowing the downsides:
 * a growing local data store and complex redux selectors that require lots of
 * prudence around caching. Part of why we could do this is that our business
 * case is well aligned with scoping: org/course/topic/page level scopes work
 * well to contain data.
 *
 * Problems potentially arise as we start querying cross-course data, or start
 * querying assessment data for e.g. the assessment dashboard, as it requires
 * basically all pages/groups/outcomes/submissions/assessments/etc to be loaded,
 * which is, essentially, our entire app content for that course scope. We can
 * only do that for so many courses... or can we? I'm a big fan of
 * measure-first-worry-later.
 */

// Overall store size warning threshold (in bytes)
export const STORE_SIZE_WARNING_BYTES = 5 * 1024 * 1024; // 5MB

// Minimum increase in total/per-slice size required before we emit another warning
// Acts as a rate limiter so we do not spam Rollbar as the store grows slowly
export const STORE_SIZE_WARNING_REPEAT_THRESHOLD_BYTES = 2 * 1024 * 1024; // 2MB

// Debounce for computing store size after API mutations (in milliseconds)
export const STORE_SIZE_CHECK_DEBOUNCE_MS = 5_000; // 5s
